import React, { useState, useEffect, useRef } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faFileExcel,
  faFileCsv,
  faUser,
  faFileAlt,
  faCalendarAlt,
  faCheckCircle,
  faClock,
  faTimesCircle,
  faExclamationTriangle,
  faEye,
  faEdit,
  faChartBar,
  faInfoCircle,
  faCheck,
  faVolumeDown,
  faPhoneSlash,
  faBan,
  faCalendarCheck,
  faUserSlash,
  faPhoneAlt,
  faDownload,
  faTimes,
  faPlus,
  faSearch,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import SuccessModal from '../../components/SuccessModal';

const InterviewUploadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [upload, setUpload] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [lines, setLines] = useState([]);
  const [linesPagination, setLinesPagination] = useState({});
  const [loadingLines, setLoadingLines] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const showDeactivateModalDismiss = useModalDismiss(() => setShowDeactivateModal(false), { backdropClose: false, enabled: showDeactivateModal });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showAddInterviewerModal, setShowAddInterviewerModal] = useState(false);
  const showAddInterviewerModalDismiss = useModalDismiss(() => setShowAddInterviewerModal(false), { backdropClose: false, enabled: showAddInterviewerModal });
  const [interviewers, setInterviewers] = useState([]);
  const [interviewerSearch, setInterviewerSearch] = useState('');
  const [showInterviewerDropdown, setShowInterviewerDropdown] = useState(false);
  const [selectedInterviewers, setSelectedInterviewers] = useState([]);
  const [addingInterviewers, setAddingInterviewers] = useState(false);
  const [removingInterviewer, setRemovingInterviewer] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const interviewerDropdownRef = useRef(null);

  useEffect(() => {
    fetchUploadDetails();
    fetchInterviewers();
  }, [id]);

  useEffect(() => {
    if (upload) {
      fetchLines();
    }
  }, [upload, statusFilter, currentPage]);

  // Handle click outside to close interviewer dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (interviewerDropdownRef.current && !interviewerDropdownRef.current.contains(event.target)) {
        setShowInterviewerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchUploadDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/cati/interview-uploads/${id}`);
      
      if (response.data.success) {
        setUpload(response.data.data);
        setStats(response.data.stats);
      } else {
        setError(response.data.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error('Error fetching upload details:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement du fichier');
    } finally {
      setLoading(false);
    }
  };

  const fetchInterviewers = async () => {
    try {
      const response = await api.get('/cati/interviewers');
      if (response.data.success) {
        setInterviewers(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching interviewers:', error);
    }
  };

  const fetchLines = async () => {
    try {
      setLoadingLines(true);
      const params = new URLSearchParams({
        page: currentPage,
        per_page: 20,
        upload_id: id
      });
      
      if (statusFilter !== 'all') {
        if (statusFilter === 'pending') {
          params.append('completed', 'false');
        } else if (statusFilter === 'completed') {
          params.append('completed', 'true');
        } else {
          params.append('result_status', statusFilter);
        }
      }

      const response = await api.get(`/cati/interviewer/lines?${params}`);
      
      if (response.data.success) {
        const data = response.data.data;
        setLines(data.data || []);
        setLinesPagination({
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
          from: data.from,
          to: data.to
        });
      }
    } catch (err) {
      console.error('Error fetching lines:', err);
    } finally {
      setLoadingLines(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await api.get(`/cati/interview-uploads/${id}/export`, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `export_${upload?.file_name || 'upload'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Erreur lors de l\'exportation');
    } finally {
      setExporting(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      setDeactivating(true);
      const response = await api.put(`/cati/interview-uploads/${id}/status`, {
        status: 'reported'
      });
      
      if (response.data.success) {
        // Update the upload status in state
        setUpload(response.data.data);
        setShowDeactivateModal(false);
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error deactivating upload:', error);
      alert(error.response?.data?.message || 'Erreur lors de la désactivation');
    } finally {
      setDeactivating(false);
    }
  };

  const handleInterviewerSearch = (value) => {
    setInterviewerSearch(value);
    setShowInterviewerDropdown(true);
  };

  const handleInterviewerSelect = (interviewer) => {
    // Check if interviewer is already selected
    if (selectedInterviewers.find(i => i.id === interviewer.id)) {
      return; // Already selected, do nothing
    }
    
    // Check if interviewer is already assigned to this upload
    const existingIds = upload?.assignments?.map(a => a.interviewer_id) || [];
    if (existingIds.includes(interviewer.id)) {
      return; // Already assigned, do nothing
    }
    
    // Add to selected list
    const newSelected = [...selectedInterviewers, interviewer];
    setSelectedInterviewers(newSelected);
    setInterviewerSearch('');
    setShowInterviewerDropdown(false);
  };

  const removeSelectedInterviewer = (interviewerId) => {
    const newSelected = selectedInterviewers.filter(i => i.id !== interviewerId);
    setSelectedInterviewers(newSelected);
  };

  const handleAddInterviewers = async () => {
    if (selectedInterviewers.length === 0) {
      alert('Veuillez sélectionner au moins un enquêteur');
      return;
    }

    try {
      setAddingInterviewers(true);
      const response = await api.post(`/cati/interview-uploads/${id}/interviewers`, {
        interviewer_ids: selectedInterviewers.map(i => i.id)
      });

      if (response.data.success) {
        // Update upload with new assignments
        setUpload(response.data.data);
        setShowAddInterviewerModal(false);
        setSelectedInterviewers([]);
        setInterviewerSearch('');
        setShowSuccessModal(true);
        // Refresh upload details
        await fetchUploadDetails();
      } else {
        alert(response.data.message || 'Erreur lors de l\'ajout des enquêteurs');
      }
    } catch (error) {
      console.error('Error adding interviewers:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'ajout des enquêteurs');
    } finally {
      setAddingInterviewers(false);
    }
  };

  const handleRemoveInterviewer = async () => {
    if (!removingInterviewer) return;

    try {
      setAddingInterviewers(true);
      const response = await api.delete(`/cati/interview-uploads/${id}/interviewers/${removingInterviewer.interviewer_id}`);

      if (response.data.success) {
        // Update upload with removed assignment
        setUpload(response.data.data);
        const removedName = removingInterviewer.interviewer?.name;
        setRemovingInterviewer(null);
        setShowRemoveModal(false);
        setShowSuccessModal(true);
        // Refresh upload details
        await fetchUploadDetails();
      } else {
        alert(response.data.message || 'Erreur lors du retrait de l\'enquêteur');
      }
    } catch (error) {
      console.error('Error removing interviewer:', error);
      alert(error.response?.data?.message || 'Erreur lors du retrait de l\'enquêteur');
    } finally {
      setAddingInterviewers(false);
    }
  };

  const filteredInterviewers = interviewers.filter(interviewer => {
    const matchesSearch = interviewer.name.toLowerCase().includes(interviewerSearch.toLowerCase()) ||
                         interviewer.email.toLowerCase().includes(interviewerSearch.toLowerCase());
    const isCati = interviewer.is_cati;
    const notAlreadySelected = !selectedInterviewers.find(i => i.id === interviewer.id);
    // Check if already assigned to this upload
    const existingIds = upload?.assignments?.map(a => a.interviewer_id) || [];
    const notAlreadyAssigned = !existingIds.includes(interviewer.id);
    return matchesSearch && isCati && notAlreadySelected && notAlreadyAssigned;
  });

  const getFileIcon = (fileName) => {
    if (fileName?.match(/\.csv$/i)) {
      return faFileCsv;
    }
    return faFileExcel;
  };

  const getFileIconColor = (fileName) => {
    if (fileName?.match(/\.csv$/i)) {
      return 'text-green-600';
    }
    return 'text-blue-600';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'done': { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminé' },
      'reported': { bg: 'bg-red-100', text: 'text-red-800', label: 'Désactivé' },
      'not_done': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Non terminé' }
    };
    
    const config = statusConfig[status] || statusConfig['not_done'];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getResultStatusBadge = (line) => {
    // Check if line is blacklisted (either via is_blacklisted or is_blacklisted_computed)
    const isBlacklisted = line?.is_blacklisted || line?.is_blacklisted_computed;
    
    // If blacklisted, show blacklisted status regardless of result_status
    if (isBlacklisted && !line?.result_status) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-black text-white">
          <FontAwesomeIcon icon={faBan} className="mr-1" />
          Blacklisted
        </span>
      );
    }
    
    const status = line?.result_status;
    const statusConfig = {
      'ok': { bg: 'bg-green-100', text: 'text-green-800', label: 'OK', icon: faCheckCircle },
      'boite_vocal': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Boîte Vocale', icon: faVolumeDown },
      'refus': { bg: 'bg-red-100', text: 'text-red-800', label: 'Refus', icon: faTimesCircle },
      'hors_cible': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Hors Cible', icon: faBan },
      'rendez_vous': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Rendez-vous', icon: faCalendarCheck },
      'injoignable': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Injoignable', icon: faPhoneSlash },
      'faux_numero': { bg: 'bg-pink-100', text: 'text-pink-800', label: 'Faux Numéro', icon: faPhoneAlt }
    };
    
    if (!status) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          En attente
        </span>
      );
    }
    
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status, icon: faInfoCircle };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <FontAwesomeIcon icon={config.icon} className="mr-1" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-6xl text-red-600 animate-spin mb-4" />
          <p className="text-gray-600">Chargement des détails...</p>
        </div>
      </div>
    );
  }

  if (error || !upload) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-6xl text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-4">{error || 'Fichier non trouvé'}</p>
          <button
            onClick={() => navigate('/cati/interview-uploads')}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  const completionPercentage = stats?.total > 0 
    ? Math.round((stats.completed / stats.total) * 100) 
    : 0;

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{upload.file_name}</h1>
                <p className="text-gray-600 mt-1">Détails du fichier de la base de données CATI</p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:space-x-4">
                {upload.status !== 'reported' && (user?.role === 'resp_cati' || user?.role === 'supervisor_cati') && (
                  <button
                    onClick={() => setShowDeactivateModal(true)}
                    className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <FontAwesomeIcon icon={faBan} className="mr-2" />
                    Désactiver
                  </button>
                )}
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exporting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                      Export en cours...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faDownload} className="mr-2" />
                      Exporter
                    </>
                  )}
                </button>
                <div className="flex justify-center sm:justify-start">
                  {getStatusBadge(upload.status)}
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Lignes</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <FontAwesomeIcon icon={faFileAlt} className="text-2xl text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Complétées</p>
                    <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
                    
                  </div>
                  <div className="bg-green-100 p-3 rounded-lg">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-2xl text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">En attente</p>
                    <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
                  </div>
                  <div className="bg-yellow-100 p-3 rounded-lg">
                    <FontAwesomeIcon icon={faClock} className="text-2xl text-yellow-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Réussies (OK)</p>
                    <p className="text-3xl font-bold text-green-600">{stats.by_status.ok}</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-lg">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-2xl text-green-600" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Status Breakdown */}
          {stats && (
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-6">
              
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                {Object.entries(stats.by_status).map(([status, count]) => {
                  if (status === 'null') return null;
                  const statusLabels = {
                    'ok': 'OK',
                    'boite_vocal': 'Boîte Vocale',
                    'refus': 'Refus',
                    'hors_cible': 'Hors Cible',
                    'rendez_vous': 'Rendez-vous',
                    'injoignable': 'Injoignable',
                    'faux_numero': 'Faux Numéro'
                  };
                  return (
                    <div key={status} className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{count}</p>
                      <p className="text-xs text-gray-600 mt-1">{statusLabels[status] || status}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Left: Upload Info */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 sm:p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Informations</h2>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Enquête</p>
                    <p className="text-sm sm:text-base font-medium text-gray-900 break-words">
                      {upload.survey?.title || '-'}
                    </p>
                    {upload.survey?.limesurvey_id && (
                      <p className="text-xs text-gray-500 mt-1">
                        ID: {upload.survey.limesurvey_id}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-gray-500">
                        {upload.assignments && upload.assignments.length > 1 ? 'Enquêteurs' : 'Enquêteur'}
                      </p>
                      {(user?.role === 'resp_cati' || user?.role === 'supervisor_cati') && (
                        <button
                          onClick={() => setShowAddInterviewerModal(true)}
                          className="text-xs text-red-600 hover:text-red-700 flex items-center flex-shrink-0 ml-2"
                        >
                          <FontAwesomeIcon icon={faPlus} className="mr-1" />
                          Ajouter
                        </button>
                      )}
                    </div>
                    {upload.assignments && upload.assignments.length > 0 ? (
                      <div className="space-y-2 mt-1">
                        {upload.assignments.map((assignment, index) => (
                          <div key={assignment.id || index} className="flex items-start sm:items-center justify-between group gap-2">
                            <div className="flex items-start sm:items-center flex-1 min-w-0">
                              <FontAwesomeIcon icon={faUser} className="text-gray-400 mr-2 flex-shrink-0 mt-0.5 sm:mt-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm sm:text-base font-medium text-gray-900 break-words">
                                  {assignment.interviewer?.name || '-'}
                                </p>
                                {assignment.interviewer?.email && (
                                  <p className="text-xs text-gray-500 break-all">{assignment.interviewer.email}</p>
                                )}
                              </div>
                            </div>
                            {(user?.role === 'resp_cati' || user?.role === 'supervisor_cati') && (
                              <button
                                onClick={() => {
                                  setRemovingInterviewer(assignment);
                                  setShowRemoveModal(true);
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-700 ml-2 flex-shrink-0"
                                title="Retirer l'enquêteur"
                              >
                                <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center mt-1">
                        <FontAwesomeIcon icon={faUser} className="text-gray-400 mr-2 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm sm:text-base font-medium text-gray-900 break-words">
                            {upload.interviewer?.name || '-'}
                          </p>
                          {upload.interviewer?.email && (
                            <p className="text-xs text-gray-500 break-all">{upload.interviewer.email}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 mb-1">Téléchargé le</p>
                    <div className="flex items-center mt-1">
                      <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 mr-2 flex-shrink-0" />
                      <p className="text-sm sm:text-base text-gray-900 break-words">
                        {formatDate(upload.created_at)}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 mb-1">Téléchargé par</p>
                    <p className="text-sm sm:text-base font-medium text-gray-900 break-words">
                      {upload.uploaded_by?.name || '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Lines Table */}
            <div className="lg:col-span-2">
              {/* Filter - Outside card on small screens */}
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h2 className="text-xl font-bold text-gray-900">Lignes des questionnaires</h2>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="pending">En attente</option>
                  <option value="completed">Complétées</option>
                  <option value="blacklisted">Blacklisted</option>
                  <option value="ok">OK</option>
                  <option value="boite_vocal">Boîte Vocale</option>
                  <option value="refus">Refus</option>
                  <option value="hors_cible">Hors Cible</option>
                  <option value="rendez_vous">Rendez-vous</option>
                  <option value="injoignable">Injoignable</option>
                  <option value="faux_numero">Faux Numéro</option>
                </select>
              </div>

              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 sm:p-6">

                {loadingLines ? (
                  <div className="text-center py-12">
                    <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-600 animate-spin mb-4" />
                    <p className="text-gray-600">Chargement des lignes...</p>
                  </div>
                ) : lines.length === 0 ? (
                  <div className="text-center py-12">
                    <FontAwesomeIcon icon={faFileAlt} className="text-6xl text-gray-300 mb-4" />
                    <p className="text-gray-600">Aucune ligne trouvée</p>
                  </div>
                ) : (
                  <>
                    {(() => {
                      const hasCodeAgence = lines.some(line => line.code_agence);
                      return (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Statut
                                </th>
                                {hasCodeAgence && (
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Code Agence
                                  </th>
                                )}
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Num_tel
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Complété le
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {lines.map((line) => (
                                <tr 
                                  key={line.id} 
                                  onClick={() => navigate(`/cati/interview-uploads/${id}/lines/${line.id}`)}
                                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                                >
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    {getResultStatusBadge(line)}
                                  </td>
                                  {hasCodeAgence && (
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                      {line.code_agence || '-'}
                                    </td>
                                  )}
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                    {line.line_data?.Num_tel || line.line_data?.num_tel || line.line_data?.['Num_tel'] || '-'}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                    {formatDate(line.completed_at)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}

                  </>
                )}
              </div>

              {/* Pagination - Outside card */}
              {!loadingLines && lines.length > 0 && linesPagination.last_page > 1 && (
                <div className="mt-4 bg-white rounded-xl shadow-lg border border-gray-100 p-4 sm:p-6">
                  {/* Pagination controls */}
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    {/* Mobile: Show page info at top */}
                    <div className="sm:hidden text-sm text-gray-700 text-center w-full">
                      Page {linesPagination.current_page} sur {linesPagination.last_page}
                    </div>
                    
                    {/* Desktop: Show full info */}
                    <div className="hidden sm:block text-sm text-gray-700">
                      <span>
                        Affichage de <span className="font-medium">{linesPagination.from}</span> à{' '}
                        <span className="font-medium">{linesPagination.to}</span> sur{' '}
                        <span className="font-medium">{linesPagination.total}</span> résultats
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 w-full sm:w-auto justify-center">
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Précédent
                      </button>
                      <span className="hidden sm:inline px-3 py-2 text-sm text-gray-700">
                        Page {linesPagination.current_page} sur {linesPagination.last_page}
                      </span>
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === linesPagination.last_page}
                        className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Deactivate Confirmation Modal */}
      {showDeactivateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                  <FontAwesomeIcon icon={faBan} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Désactiver l'upload
                  </h3>
                  <p className="text-sm text-gray-500">
                    Cette action est irréversible
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowDeactivateModal(false)}
                disabled={deactivating}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 mb-6">
                Êtes-vous sûr de vouloir désactiver cet upload ? Les lignes ne seront plus disponibles pour l'enquêteur.
              </p>

              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowDeactivateModal(false)}
                  disabled={deactivating}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeactivate}
                  disabled={deactivating}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {deactivating ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Désactivation...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faBan} className="mr-2" />
                      Désactiver
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          setRemovingInterviewer(null);
        }}
        message={
          upload?.status === 'reported' 
            ? "Upload désactivé avec succès" 
            : removingInterviewer 
              ? "Enquêteur retiré avec succès"
              : "Enquêteurs ajoutés avec succès"
        }
      />

      {/* Add Interviewer Modal */}
      {showAddInterviewerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <FontAwesomeIcon icon={faPlus} className="text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Ajouter des enquêteurs
                  </h3>
                  <p className="text-sm text-gray-500">
                    Sélectionnez les enquêteurs à ajouter à cet upload
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAddInterviewerModal(false);
                  setSelectedInterviewers([]);
                  setInterviewerSearch('');
                }}
                disabled={addingInterviewers}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Interviewer Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rechercher un enquêteur
                </label>
                <div className="relative" ref={interviewerDropdownRef}>
                  <div className="relative">
                    <input
                      type="text"
                      value={interviewerSearch}
                      onChange={(e) => handleInterviewerSearch(e.target.value)}
                      onFocus={() => setShowInterviewerDropdown(true)}
                      placeholder="Rechercher par nom ou email..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                  </div>

                  {/* Dropdown */}
                  {showInterviewerDropdown && filteredInterviewers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredInterviewers.map((interviewer) => (
                        <div
                          key={interviewer.id}
                          onClick={() => handleInterviewerSelect(interviewer)}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faUser} className="text-gray-400 mr-2" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {interviewer.name}
                              </p>
                              <p className="text-xs text-gray-500">{interviewer.email}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Interviewers */}
              {selectedInterviewers.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enquêteurs sélectionnés ({selectedInterviewers.length})
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {selectedInterviewers.map((interviewer) => (
                      <div
                        key={interviewer.id}
                        className="flex items-center bg-gray-100 rounded-lg px-3 py-2"
                      >
                        <FontAwesomeIcon icon={faUser} className="text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {interviewer.name}
                        </span>
                        <button
                          onClick={() => removeSelectedInterviewer(interviewer.id)}
                          className="ml-2 text-gray-400 hover:text-red-600"
                        >
                          <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddInterviewerModal(false);
                    setSelectedInterviewers([]);
                    setInterviewerSearch('');
                  }}
                  disabled={addingInterviewers}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleAddInterviewers}
                  disabled={addingInterviewers || selectedInterviewers.length === 0}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {addingInterviewers ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Ajout en cours...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlus} className="mr-2" />
                      Ajouter
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Interviewer Modal */}
      {showRemoveModal && removingInterviewer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                  <FontAwesomeIcon icon={faTrash} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Retirer l'enquêteur
                  </h3>
                  <p className="text-sm text-gray-500">
                    Cette action est irréversible
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowRemoveModal(false);
                  setRemovingInterviewer(null);
                }}
                disabled={addingInterviewers}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Êtes-vous sûr de vouloir retirer <strong>{removingInterviewer.interviewer?.name}</strong> de cet upload ?
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Les lignes déjà complétées par cet enquêteur resteront en son nom. 
                  Il ne recevra plus de nouvelles lignes et ne pourra plus soumettre de résultats pour cet upload.
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowRemoveModal(false);
                    setRemovingInterviewer(null);
                  }}
                  disabled={addingInterviewers}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleRemoveInterviewer}
                  disabled={addingInterviewers}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {addingInterviewers ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Retrait en cours...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faTrash} className="mr-2" />
                      Retirer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewUploadDetail;

